<script setup lang="ts">
import Button from '@/Components/Ui/Button.vue';
import Progress from '@/Components/Ui/Progress.vue';
import { useTheme } from '@/composables/useTheme';
import PublicLayout from '@/Layouts/PublicLayout.vue';
import { dashboard, landing, login, register } from '@/routes';
import { switchMethod as switchLanguage } from '@/routes/language';
import { Head, Link, usePage } from '@inertiajs/vue3';
import {
    ArrowDown,
    ArrowRight,
    ArrowUpRight,
    Check,
    ChevronDown,
    Moon,
    Sun,
    Users,
} from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import LandingCapabilities from './Partials/LandingCapabilities.vue';
import LandingPreview from './Partials/LandingPreview.vue';

interface Props {
    seo: {
        title: string;
        description: string;
        canonical: string;
        image: string;
        locale: string;
    };
    apiDocsUrl?: string | null;
}
defineProps<Props>();
const content = ref<HTMLElement | null>(null);
let entranceObserver: IntersectionObserver | undefined;

onMounted(() => {
    if (
        !('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
        return;
    // Content is visible by default; the observer only adds a short decorative entrance.
    entranceObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                entry.target.classList.add('is-entered');
                entranceObserver?.unobserve(entry.target);
            }
        },
        { threshold: 0.15 },
    );
    content.value
        ?.querySelectorAll('[data-landing-motion]')
        .forEach((element) => entranceObserver?.observe(element));
});
onUnmounted(() => entranceObserver?.disconnect());

const page = usePage();
const { isDark, setTheme } = useTheme();
const isAuthenticated = computed(() => !!page.props.auth?.user);
const primaryRoute = computed(() =>
    isAuthenticated.value ? dashboard() : register(),
);
const primaryLabel = computed(() =>
    isAuthenticated.value ? 'landing.nav.dashboard' : 'landing.nav.register',
);
const otherLocale = computed(() =>
    page.props.app.lang === 'en' ? 'fr' : 'en',
);
const money = (cents: number) =>
    new Intl.NumberFormat(page.props.app.lang === 'en' ? 'en-IE' : 'fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(cents / 100);
</script>

<template>
    <PublicLayout presentation="landing" class="landing-page">
        <Head :title="$t('landing.seo.title')">
            <meta
                head-key="description"
                name="description"
                :content="seo.description"
            />
            <link head-key="canonical" rel="canonical" :href="seo.canonical" />
            <meta head-key="og:type" property="og:type" content="website" />
            <meta
                head-key="og:title"
                property="og:title"
                :content="seo.title"
            />
            <meta
                head-key="og:description"
                property="og:description"
                :content="seo.description"
            />
            <meta
                head-key="og:url"
                property="og:url"
                :content="seo.canonical"
            />
            <meta
                head-key="og:image"
                property="og:image"
                :content="seo.image"
            />
            <meta
                head-key="og:locale"
                property="og:locale"
                :content="seo.locale"
            />
            <meta
                head-key="twitter:card"
                name="twitter:card"
                content="summary"
            />
            <meta
                head-key="twitter:title"
                name="twitter:title"
                :content="seo.title"
            />
            <meta
                head-key="twitter:description"
                name="twitter:description"
                :content="seo.description"
            />
            <meta
                head-key="twitter:image"
                name="twitter:image"
                :content="seo.image"
            />
        </Head>
        <template #header>
            <a
                href="#main-content"
                class="bg-ink text-canvas sr-only z-50 rounded-md px-5 py-3 focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
                >{{ $t('landing.nav.skip') }}</a
            >
            <header
                class="landing-width border-border flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-b py-4 sm:py-5"
            >
                <Link
                    :href="landing()"
                    aria-label="Finzzo"
                    class="font-display text-3xl font-semibold tracking-tight sm:text-4xl"
                    >Finzzo<span class="text-terracotta" aria-hidden="true"
                        >.</span
                    ></Link
                >
                <nav
                    :aria-label="$t('landing.nav.label')"
                    class="hidden items-center gap-6 text-sm lg:flex"
                >
                    <a href="#au-quotidien" class="landing-nav">{{
                        $t('landing.nav.daily')
                    }}</a>
                    <a href="#ensemble" class="landing-nav">{{
                        $t('landing.nav.together')
                    }}</a>
                    <a href="#questions" class="landing-nav">{{
                        $t('landing.nav.questions')
                    }}</a>
                </nav>
                <div class="flex flex-wrap items-center gap-1 sm:gap-3">
                    <a
                        :href="switchLanguage(otherLocale).url"
                        :lang="otherLocale"
                        :aria-label="$t('landing.nav.language')"
                        class="hover:bg-surface-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-xs font-semibold uppercase"
                        >{{ otherLocale }}</a
                    >
                    <Button
                        variant="secondary"
                        size="icon"
                        class="min-h-11 min-w-11"
                        :aria-label="
                            $t(
                                isDark
                                    ? 'landing.nav.light'
                                    : 'landing.nav.dark',
                            )
                        "
                        @click="setTheme(isDark ? 'light' : 'dark')"
                        ><Sun
                            v-if="isDark"
                            class="size-4"
                            aria-hidden="true" /><Moon
                            v-else
                            class="size-4"
                            aria-hidden="true"
                    /></Button>
                    <Link
                        :href="isAuthenticated ? dashboard() : login()"
                        class="hover:bg-surface-2 inline-flex min-h-11 items-center justify-center rounded-md px-2 text-xs font-medium sm:px-3 sm:text-sm"
                        >{{
                            $t(
                                isAuthenticated
                                    ? 'landing.nav.dashboard'
                                    : 'landing.nav.login',
                            )
                        }}</Link
                    >
                </div>
            </header>
        </template>

        <div ref="content" class="landing-width">
            <section
                class="grid gap-8 pt-12 pb-10 sm:pt-16 sm:pb-12 lg:grid-cols-[1.55fr_1fr] lg:items-end lg:gap-16 lg:pt-20 lg:pb-14"
                aria-labelledby="landing-title"
            >
                <div>
                    <p class="landing-eyebrow mb-6">
                        {{ $t('landing.hero.eyebrow') }}
                    </p>
                    <h1
                        id="landing-title"
                        class="font-display text-[clamp(2.8rem,6.4vw,6rem)] leading-[1.02] font-normal tracking-[-0.045em] text-balance"
                    >
                        {{ $t('landing.hero.title') }}
                        <span class="text-terracotta block">{{
                            $t('landing.hero.accent')
                        }}</span>
                    </h1>
                </div>
                <div class="lg:pb-2">
                    <p
                        class="text-text-soft max-w-lg text-base leading-relaxed sm:text-lg"
                    >
                        {{ $t('landing.hero.description') }}
                    </p>
                    <div
                        class="mt-7 flex flex-wrap items-center gap-x-6 gap-y-4"
                    >
                        <Link :href="primaryRoute" class="landing-cta"
                            >{{ $t(primaryLabel)
                            }}<ArrowUpRight
                                class="size-4 shrink-0"
                                aria-hidden="true"
                        /></Link>
                        <a
                            href="#apercu"
                            class="decoration-border inline-flex min-h-11 items-center gap-2 text-sm font-medium underline underline-offset-4"
                            >{{ $t('landing.hero.explore')
                            }}<ArrowDown
                                class="size-4 shrink-0"
                                aria-hidden="true"
                        /></a>
                    </div>
                    <p class="text-text-soft mt-4 text-xs">
                        {{ $t('landing.hero.note') }}
                    </p>
                </div>
            </section>

            <LandingPreview />

            <section
                id="au-quotidien"
                class="landing-chapter"
                aria-labelledby="daily-title"
            >
                <div>
                    <p class="landing-eyebrow">
                        {{ $t('landing.daily.eyebrow') }}
                    </p>
                    <h2 id="daily-title" class="landing-heading">
                        {{ $t('landing.daily.title') }}
                    </h2>
                    <p class="landing-description">
                        {{ $t('landing.daily.description') }}
                    </p>
                    <p
                        class="text-text-soft mt-6 max-w-lg text-sm leading-relaxed"
                    >
                        {{ $t('landing.daily.note') }}
                    </p>
                </div>
                <div class="min-w-0">
                    <p class="landing-eyebrow mb-5">
                        {{ $t('landing.daily.example') }}
                    </p>
                    <div
                        v-for="item in [
                            { name: 'groceries', category: 'food' },
                            { name: 'transport', category: 'travel' },
                        ]"
                        :key="item.name"
                        class="border-border flex flex-wrap items-center justify-between gap-4 border-b py-5"
                    >
                        <span class="text-sm">{{
                            $t(`landing.preview.${item.name}`)
                        }}</span>
                        <span
                            class="bg-positive-soft text-ink inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium"
                            ><Check class="size-3.5" aria-hidden="true" />{{
                                $t(`landing.preview.${item.category}`)
                            }}</span
                        >
                    </div>
                    <p
                        class="font-display text-text-soft mt-6 text-2xl leading-snug"
                    >
                        {{ $t('landing.daily.classified') }}
                    </p>
                </div>
            </section>

            <section class="landing-chapter" aria-labelledby="budget-title">
                <div class="lg:order-2">
                    <p class="landing-eyebrow">
                        {{ $t('landing.budget.eyebrow') }}
                    </p>
                    <h2 id="budget-title" class="landing-heading">
                        {{ $t('landing.budget.title') }}
                    </h2>
                    <p class="landing-description">
                        {{ $t('landing.budget.description') }}
                    </p>
                    <p
                        class="text-terracotta mt-6 inline-flex items-center gap-2 text-sm font-medium"
                    >
                        <ArrowRight
                            class="size-4 shrink-0"
                            aria-hidden="true"
                        />{{ $t('landing.budget.note') }}
                    </p>
                </div>
                <div
                    class="bg-surface-2 border-border rounded-xl border p-5 sm:p-8 lg:order-1"
                >
                    <p class="landing-eyebrow mb-7">
                        {{ $t('landing.budget.example') }}
                    </p>
                    <div
                        v-for="budget in [
                            { key: 'food', spent: 32000, limit: 45000 },
                            { key: 'leisure', spent: 8500, limit: 15000 },
                        ]"
                        :key="budget.key"
                        class="mt-7"
                    >
                        <div
                            class="mb-3 flex flex-wrap items-baseline justify-between gap-3 text-sm"
                        >
                            <span class="font-medium">{{
                                $t(`landing.preview.${budget.key}`)
                            }}</span
                            ><span
                                class="font-geist-mono text-text-soft text-xs"
                                >{{ money(budget.spent) }} /
                                {{ money(budget.limit) }}</span
                            >
                        </div>
                        <Progress
                            :value="(budget.spent / budget.limit) * 100"
                            :color="
                                budget.key === 'food'
                                    ? 'positive'
                                    : 'terracotta'
                            "
                            :aria-label="$t(`landing.preview.${budget.key}`)"
                            :aria-valuetext="`${money(budget.spent)} ${$t('landing.preview.spent')} ${money(budget.limit)}`"
                        />
                        <p
                            v-if="budget.key === 'food'"
                            class="text-text-soft mt-3 text-xs"
                        >
                            {{ $t('landing.budget.remaining') }}
                        </p>
                    </div>
                </div>
            </section>

            <section
                id="ensemble"
                class="border-border bg-surface-2 my-12 grid items-center gap-9 rounded-2xl border p-6 sm:my-16 sm:p-10 lg:my-20 lg:grid-cols-[1.2fr_1fr] lg:gap-20 lg:p-14"
                aria-labelledby="together-title"
            >
                <div>
                    <p class="landing-eyebrow">
                        {{ $t('landing.together.eyebrow') }}
                    </p>
                    <h2 id="together-title" class="landing-heading">
                        {{ $t('landing.together.title') }}
                    </h2>
                    <p class="landing-description">
                        {{ $t('landing.together.description') }}
                    </p>
                </div>
                <figure
                    class="bg-surface border-border rounded-xl border p-5 sm:p-7"
                >
                    <div class="mb-5 flex items-center gap-3">
                        <span
                            class="bg-terracotta-soft text-terracotta inline-flex size-10 items-center justify-center rounded-lg"
                            ><Users class="size-5" aria-hidden="true"
                        /></span>
                        <p class="font-display text-2xl">
                            {{ $t('landing.together.household') }}
                        </p>
                    </div>
                    <div
                        v-for="(member, index) in [
                            { name: 'you', role: 'owner' },
                            { name: 'person', role: 'member' },
                        ]"
                        :key="member.name"
                        class="border-border flex flex-wrap items-center gap-3 border-b py-4"
                    >
                        <span
                            class="bg-surface-2 text-text-soft inline-flex size-8 shrink-0 items-center justify-center rounded-full text-xs"
                            aria-hidden="true"
                            >{{ index + 1 }}</span
                        ><span class="flex-1 text-sm">{{
                            $t(`landing.together.${member.name}`)
                        }}</span
                        ><span class="text-text-soft text-xs">{{
                            $t(`landing.together.${member.role}`)
                        }}</span>
                    </div>
                    <p class="text-text-soft mt-5 text-sm leading-relaxed">
                        {{ $t('landing.together.note') }}
                    </p>
                    <figcaption class="text-text-soft mt-4 text-xs">
                        {{ $t('landing.together.example') }} ·
                        {{ $t('landing.preview.caption') }}
                    </figcaption>
                </figure>
            </section>

            <LandingCapabilities />

            <section
                id="questions"
                class="grid gap-7 py-8 sm:gap-12 sm:pb-16 lg:grid-cols-[1fr_1.2fr] lg:gap-20"
                aria-labelledby="questions-title"
            >
                <div>
                    <p class="landing-eyebrow">
                        {{ $t('landing.faq.eyebrow') }}
                    </p>
                    <h2 id="questions-title" class="landing-heading">
                        {{ $t('landing.faq.title') }}
                    </h2>
                </div>
                <div>
                    <details
                        v-for="topic in ['bank', 'solo', 'start', 'data']"
                        :key="topic"
                        class="group border-border border-b py-5"
                    >
                        <summary
                            class="flex cursor-pointer list-none items-center justify-between gap-5 text-sm leading-relaxed font-medium sm:text-base"
                        >
                            {{ $t(`landing.faq.${topic}_question`)
                            }}<ChevronDown
                                class="size-4 shrink-0 group-open:rotate-180"
                                aria-hidden="true"
                            />
                        </summary>
                        <p
                            class="text-text-soft mt-4 pr-6 text-sm leading-relaxed"
                        >
                            {{ $t(`landing.faq.${topic}_answer`) }}
                        </p>
                    </details>
                </div>
            </section>

            <section
                class="border-border border-t py-14 text-center sm:py-20"
                aria-labelledby="closing-title"
            >
                <p class="landing-eyebrow">
                    {{ $t('landing.closing.eyebrow') }}
                </p>
                <h2
                    id="closing-title"
                    class="landing-heading mx-auto max-w-3xl"
                >
                    {{ $t('landing.closing.title') }}
                </h2>
                <p class="landing-description mx-auto max-w-xl">
                    {{ $t('landing.closing.description') }}
                </p>
                <Link :href="primaryRoute" class="landing-cta mt-7"
                    >{{ $t(primaryLabel)
                    }}<ArrowUpRight class="size-4 shrink-0" aria-hidden="true"
                /></Link>
            </section>
        </div>

        <template #footer>
            <footer
                class="landing-width border-border flex flex-col justify-between gap-6 border-t py-7 sm:flex-row sm:items-center"
            >
                <div>
                    <Link
                        :href="landing()"
                        aria-label="Finzzo"
                        class="font-display text-3xl font-semibold tracking-tight"
                        >Finzzo<span class="text-terracotta" aria-hidden="true"
                            >.</span
                        ></Link
                    >
                    <p class="text-text-soft mt-1 text-xs">
                        {{ $t('landing.footer.tagline') }}
                    </p>
                </div>
                <nav
                    :aria-label="$t('landing.footer.label')"
                    class="text-text-soft flex flex-wrap items-center gap-x-5 gap-y-4 text-xs"
                >
                    <a href="#au-quotidien">{{ $t('landing.nav.daily') }}</a>
                    <a href="#ensemble">{{ $t('landing.nav.together') }}</a>
                    <a href="#questions">{{ $t('landing.nav.questions') }}</a>
                    <a
                        v-if="page.props.api_docs_enabled && apiDocsUrl"
                        :href="apiDocsUrl"
                        >{{ $t('landing.footer.api') }}</a
                    >
                </nav>
            </footer>
        </template>
    </PublicLayout>
</template>

<style scoped>
@reference '../../../css/app.css';
.landing-width {
    @apply mx-auto w-[calc(100%-2rem)] max-w-[1200px] sm:w-[calc(100%-4rem)];
}
.landing-page :deep(.landing-eyebrow) {
    @apply text-text-soft text-[11px] leading-relaxed font-medium tracking-[0.12em] uppercase;
}
.landing-page :deep(.landing-heading) {
    @apply font-display my-5 text-4xl leading-[1.08] font-normal tracking-[-0.025em] text-balance sm:text-5xl lg:text-[3.5rem];
}
.landing-page :deep(.landing-description) {
    @apply text-text-soft text-base leading-relaxed;
}
.landing-page :deep(.landing-chapter) {
    @apply border-border grid items-center gap-9 border-b py-12 sm:gap-12 sm:py-16 lg:grid-cols-2 lg:gap-24 lg:py-20;
}
.landing-cta {
    @apply bg-terracotta hover:bg-terracotta-hover text-on-terracotta inline-flex min-h-12 items-center justify-center gap-3 rounded-lg px-6 py-3 text-sm font-semibold;
}
.landing-nav {
    @apply text-text-soft hover:text-ink inline-flex min-h-11 items-center;
}
.landing-page :deep(:is(a, button, summary):focus-visible) {
    outline: 2px solid var(--color-terracotta);
    outline-offset: 5px;
}
.landing-page :deep(section[id]) {
    scroll-margin-top: 2rem;
}
.landing-page :deep(summary::-webkit-details-marker) {
    display: none;
}
@keyframes landing-settle {
    from {
        transform: translateY(16px);
    }
    to {
        transform: translateY(0);
    }
}
@media (prefers-reduced-motion: no-preference) {
    .landing-page :deep(.landing-motion.is-entered) {
        animation: landing-settle 650ms cubic-bezier(0.2, 0.7, 0.2, 1)
            var(--landing-delay, 0ms) both;
    }
}
@media (prefers-reduced-motion: reduce) {
    .landing-page :deep(*) {
        animation: none !important;
        transition: none !important;
        scroll-behavior: auto !important;
    }
}
</style>
